import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, CheckCircle, Undo2 } from 'lucide-react';
import type { ProposalOpsReviewComment, ProposalOpsReviewMeta } from '../../types';
import {
  approveOpsReview,
  canApproveOpsReview,
  canWithdrawOpsReview,
  getOpsReviewNodeLabel,
  getProjectManagers,
  getSalesRepCandidates,
  requestOpsReview,
  withdrawOpsReview,
} from '../../utils/proposalOpsReview';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import ProposalStructureMentionText from './ProposalStructureMentionText';
import StructureMentionTextarea from './StructureMentionTextarea';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import type { StructureMentionProjectOption } from '../../utils/structureMentionProjectOptions';
import './ProposalOpsReview.css';

interface OpsReviewCommentsPanelProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  dealTitle?: string;
  meta: ProposalOpsReviewMeta | null;
  comments: ProposalOpsReviewComment[];
  canRequestReview?: boolean;
  /** Save proposal + create version snapshot before approval. Returns version id. */
  onPrepareApproval?: () => Promise<string>;
  /** Open a saved proposal version snapshot (from activity timeline). */
  onOpenVersion?: (versionId: string) => void;
  onApproved?: () => void;
  onWithdrawn?: () => void;
  onRequested?: () => void;
  defaultDealId?: string;
  defaultStructures?: StructureRef[];
  projectOptions?: StructureMentionProjectOption[];
}

interface TimelineEvent {
  id: string;
  at: Date;
  title: string;
  detail?: string;
  versionId?: string;
}

const OpsReviewCommentsPanel: React.FC<OpsReviewCommentsPanelProps> = ({
  open,
  onClose,
  dealId,
  dealTitle,
  meta,
  comments,
  canRequestReview = false,
  onPrepareApproval,
  onOpenVersion,
  onApproved,
  onWithdrawn,
  onRequested,
  defaultDealId = '',
  defaultStructures = [],
  projectOptions = [],
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [approving, setApproving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reviewerUserId, setReviewerUserId] = useState('');
  const [salesRepUserId, setSalesRepUserId] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const projectManagers = useMemo(() => getProjectManagers(crmUsers), [crmUsers]);
  const salesRepCandidates = useMemo(() => getSalesRepCandidates(crmUsers), [crmUsers]);

  useEffect(() => {
    if (!open) {
      setReviewerUserId('');
      setSalesRepUserId('');
      setRequestMessage('');
      setError(null);
    }
  }, [open]);

  const grouped = useMemo(() => {
    const structureMap = new Map<string, Map<string, ProposalOpsReviewComment[]>>();
    for (const c of comments) {
      const structKey = c.nodeSetName || c.nodeSetId;
      const nodeKey = getOpsReviewNodeLabel(c);
      if (!structureMap.has(structKey)) structureMap.set(structKey, new Map());
      const nodeMap = structureMap.get(structKey)!;
      const list = nodeMap.get(nodeKey) || [];
      list.push(c);
      nodeMap.set(nodeKey, list);
    }
    return Array.from(structureMap.entries()).map(([structureName, nodeMap]) => ({
      structureName,
      nodes: Array.from(nodeMap.entries()),
    }));
  }, [comments]);

  const timeline = useMemo((): TimelineEvent[] => {
    if (!meta) return [];
    const events: TimelineEvent[] = [];
    if (meta.requestedAt) {
      events.push({
        id: 'requested',
        at: meta.requestedAt,
        title: 'Review requested',
        detail: [
          meta.requestedByName ? `By ${meta.requestedByName}` : null,
          meta.reviewerName ? `Reviewer: ${meta.reviewerName}` : null,
          meta.salesRepName ? `Sales: ${meta.salesRepName}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      });
    }
    if (meta.requestMessage?.trim()) {
      events.push({
        id: 'request-note',
        at: meta.requestedAt || new Date(0),
        title: 'Request note',
        detail: meta.requestMessage.trim(),
      });
    }
    if (meta.approvedAt) {
      events.push({
        id: 'approved',
        at: meta.approvedAt,
        title: 'Review completed',
        detail: [
          meta.approvedByName ? `By ${meta.approvedByName}` : null,
          meta.approvedVersionId ? 'Proposal version saved' : null,
        ]
          .filter(Boolean)
          .join(' · '),
        versionId: meta.approvedVersionId,
      });
    }
    if (meta.withdrawnAt) {
      events.push({
        id: 'withdrawn',
        at: meta.withdrawnAt,
        title: 'Review withdrawn',
        detail: meta.withdrawnByName ? `By ${meta.withdrawnByName}` : undefined,
      });
    }
    return events.sort((a, b) => a.at.getTime() - b.at.getTime());
  }, [meta]);

  const canApprove = canApproveOpsReview(meta, user?.id, user?.role);
  const canWithdraw = canWithdrawOpsReview(meta, user?.id, user?.role);
  const hasActivity = timeline.length > 0 || comments.length > 0;
  const showRequestSection = canRequestReview;

  if (!open) return null;

  const formatTime = (d: Date) =>
    d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const panelTitle =
    meta?.status === 'pending'
      ? 'Pending OPS Review'
      : meta?.status === 'approved'
        ? 'Review complete'
        : 'OPS Review';

  const handleApprove = async () => {
    if (!user || !canApprove) return;
    setApproving(true);
    setError(null);
    try {
      let approvedVersionId: string | undefined;
      if (onPrepareApproval) {
        approvedVersionId = await onPrepareApproval();
      }
      await approveOpsReview({
        dealId,
        approvedBy: user.id,
        approvedByName: user.name || user.email || 'OPS',
        approvedVersionId,
      });
      onApproved?.();
      onClose();
    } catch (err) {
      console.error('[OpsReviewCommentsPanel]', err);
      setError(
        err instanceof Error && err.message.includes('save')
          ? 'Could not save a proposal version before approval. Try saving the proposal first.'
          : 'Failed to approve. Please try again.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleWithdraw = async () => {
    if (!user || !canWithdraw) return;
    if (!window.confirm('Withdraw this OPS review request? The reviewer will no longer be asked to approve.')) {
      return;
    }
    setWithdrawing(true);
    setError(null);
    try {
      await withdrawOpsReview({
        dealId,
        withdrawnBy: user.id,
        withdrawnByName: user.name || user.email || 'Staff',
      });
      onWithdrawn?.();
      onClose();
    } catch (err) {
      console.error('[OpsReviewCommentsPanel]', err);
      setError('Failed to withdraw review. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleRequestReview = async () => {
    if (!reviewerUserId || !salesRepUserId || !user) {
      setError('Select both a PM reviewer and a sales rep.');
      return;
    }
    const reviewer = projectManagers.find(u => u.uid === reviewerUserId);
    const salesRep = salesRepCandidates.find(u => u.uid === salesRepUserId);
    if (!reviewer || !salesRep) {
      setError('Invalid selection.');
      return;
    }

    setRequesting(true);
    setError(null);
    try {
      await requestOpsReview({
        dealId,
        reviewerUserId: reviewer.uid,
        reviewerName: reviewer.displayName || reviewer.email || 'PM',
        salesRepUserId: salesRep.uid,
        salesRepName: salesRep.displayName || salesRep.email || 'Sales rep',
        requestedBy: user.id,
        requestedByName: user.name || user.email || 'Staff',
        requestMessage: requestMessage.trim() || undefined,
      });
      onRequested?.();
      onClose();
    } catch (err) {
      console.error('[OpsReviewCommentsPanel]', err);
      setError('Failed to submit review request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const requestSectionTitle = hasActivity ? 'Request new review' : 'Request OPS review';

  return (
    <div className="por-overlay" onClick={onClose}>
      <div className="por-modal por-panel por-review-summary-modal" onClick={e => e.stopPropagation()}>
        <div className="por-header">
          <h2>{panelTitle}</h2>
          <button type="button" className="por-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="por-body">
          {meta && (
            <div className="por-review-status-row">
              {meta.status === 'pending' && (
                <span className="por-status-badge pending">Pending review</span>
              )}
              {meta.status === 'approved' && (
                <span className="por-status-badge approved">Approved</span>
              )}
              {meta.status === 'none' && meta.withdrawnAt && (
                <span className="por-status-badge withdrawn">Withdrawn</span>
              )}
            </div>
          )}

          {timeline.length > 0 && (
            <div className="por-review-timeline">
              <h3 className="por-review-section-title">Activity</h3>
              <ul className="por-review-timeline-list">
                {timeline.map(ev => (
                  <li key={ev.id} className="por-review-timeline-item">
                    <div className="por-review-timeline-dot" aria-hidden />
                    <div className="por-review-timeline-content">
                      <div className="por-review-timeline-title">{ev.title}</div>
                      <div className="por-review-timeline-time">{formatTime(ev.at)}</div>
                      {ev.detail && (
                        <div className="por-review-timeline-detail">
                          <ProposalStructureMentionText
                            message={ev.detail}
                            defaultDealId={defaultDealId}
                            defaultStructures={defaultStructures}
                          />
                        </div>
                      )}
                      {ev.versionId && onOpenVersion && (
                        <button
                          type="button"
                          className="por-review-version-link"
                          onClick={() => onOpenVersion(ev.versionId!)}
                        >
                          View approved proposal
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {comments.length > 0 && (
            <div className="por-review-comments-section">
              <h3 className="por-review-section-title">Comments by structure</h3>
              {grouped.map(({ structureName, nodes }) => (
                <div key={structureName} className="por-comment-group">
                  <div className="por-comment-group-title">{structureName}</div>
                  {nodes.map(([nodeLabel, items]) => (
                    <div key={nodeLabel} className="por-comment-node-group">
                      <div className="por-comment-node-title">{nodeLabel}</div>
                      {items.map(c => (
                        <div key={c.id} className="por-comment-item">
                          <div className="por-comment-meta">
                            {c.authorName} · {formatTime(c.createdAt)}
                          </div>
                          <div className="por-comment-text">
                            <ProposalStructureMentionText
                              message={c.message}
                              defaultDealId={defaultDealId}
                              defaultStructures={defaultStructures}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {showRequestSection && (
            <div className={`por-request-section ${hasActivity ? 'has-activity' : ''}`}>
              <h3 className="por-review-section-title">{requestSectionTitle}</h3>
              {dealTitle && !hasActivity && (
                <p className="por-meta-summary">
                  Request internal OPS review for <strong>{dealTitle}</strong> before sending to the client.
                </p>
              )}

              <div className="por-field">
                <label>PM reviewer</label>
                {projectManagers.length === 0 ? (
                  <div className="por-picker-empty">
                    No employees designated as PM. Add the designation in Settings → Users.
                  </div>
                ) : (
                  <div className="por-picker-list">
                    {projectManagers.map(pm => (
                      <button
                        key={pm.uid}
                        type="button"
                        className={`por-picker-item ${reviewerUserId === pm.uid ? 'active' : ''}`}
                        onClick={() => setReviewerUserId(pm.uid)}
                      >
                        <span className="por-picker-name">{pm.displayName}</span>
                        {pm.title && <span className="por-picker-title">{pm.title}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="por-field">
                <label>Sales rep (notified on approval)</label>
                <div className="por-picker-list">
                  {salesRepCandidates.map(rep => (
                    <button
                      key={rep.uid}
                      type="button"
                      className={`por-picker-item ${salesRepUserId === rep.uid ? 'active' : ''}`}
                      onClick={() => setSalesRepUserId(rep.uid)}
                    >
                      <span className="por-picker-name">{rep.displayName}</span>
                      {rep.title && <span className="por-picker-title">{rep.title}</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="por-field">
                <label>Note for reviewer (optional)</label>
                <StructureMentionTextarea
                  className="por-textarea"
                  value={requestMessage}
                  onChange={setRequestMessage}
                  defaultDealId={defaultDealId}
                  defaultStructures={defaultStructures}
                  projectOptions={projectOptions}
                  enableProjectPicker={projectOptions.length > 0}
                  placeholder="Any context for the OPS reviewer… (/ to tag a structure)"
                />
              </div>

              <button
                type="button"
                className="por-btn por-btn-primary por-request-submit"
                onClick={() => void handleRequestReview()}
                disabled={requesting || !reviewerUserId || !salesRepUserId}
              >
                {requesting ? (
                  <>
                    <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Submitting…
                  </>
                ) : (
                  'Request review'
                )}
              </button>
            </div>
          )}

          {!hasActivity && !showRequestSection && (
            <div className="por-empty">No review activity yet.</div>
          )}

          {error && <p className="por-error-text">{error}</p>}
        </div>
        <div className="por-footer por-review-footer">
          <div className="por-review-footer-left">
            {canWithdraw && (
              <button
                type="button"
                className="por-btn por-btn-withdraw"
                onClick={() => void handleWithdraw()}
                disabled={withdrawing || approving}
              >
                {withdrawing ? (
                  <>
                    <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Withdrawing…
                  </>
                ) : (
                  <>
                    <Undo2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Withdraw review
                  </>
                )}
              </button>
            )}
          </div>
          <div className="por-review-footer-right">
            <button type="button" className="por-btn por-btn-secondary" onClick={onClose}>
              Close
            </button>
            {canApprove && meta?.status === 'pending' && (
              <button
                type="button"
                className="por-btn por-btn-approve"
                onClick={() => void handleApprove()}
                disabled={approving || withdrawing}
              >
                {approving ? (
                  <>
                    <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Submitting…
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Submit Review
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsReviewCommentsPanel;
